
import {catchError} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ExceptionService, PaginatedList, UtilsService } from '../../../xform-compat';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CompanySpace, SpaceType } from '../../../shared/models';
import { CONFIG } from '../../../config';


/**
 * Data service for company spaces
 */
@Injectable({
  providedIn: 'root'
})
export class CompanySpaceService {

  constructor(
    private http: HttpClient,
    private exceptionsService: ExceptionService,
    private utilsService: UtilsService
  ) { }

  /**
   * Get all spaces for a single company
   * @param {number} companyId
   * @param params: extra parameters
   * @returns {Observable<any>}: response from the API with the list of spaces
   */
  public getCompanySpaces (companyId: number, params?: any) {
    const url = `/api/rms/companies/${companyId}/spaces`;
    const httpParams = this.utilsService.generateParams(params);
    return this.http.get(url, {params: httpParams}).pipe(catchError(ex => this.exceptionsService.catchBadResponse(ex)));
  }

  /**
   * Gets a single space for a company
   * @param {number} companyId:
   * @param {number} spaceId
   * @param params
   * @returns {Observable<any>}
   */
  public getSingleSpace (companyId: number, spaceId: number, params?: any): Observable<CompanySpace> {
    const httpParams = this.utilsService.generateParams(params);
    const url = `/api/rms/companies/${companyId}/spaces/${spaceId}`;
    return this.http.get(url, {params: httpParams}).pipe(catchError(ex => this.exceptionsService.catchBadResponse(ex)));
  }

  /**
   * Gets the list of company types
   * To get the totals, requires the extrafield param: extrafields: 'totals'
   * @param params
   * @returns {Observable<PaginatedList<SpaceType>>}
   */
  public getSpaceTypes (params?: any): Observable<PaginatedList<SpaceType>> {
    const httpParams = this.utilsService.generateParams(params);
    return this.http.get(`${CONFIG.apiUrl}/rms/spacetypes`, {params: httpParams}).pipe(catchError(ex => this.exceptionsService.catchBadResponse(ex)));
  }

  /**
   * Updates an existing company space
   * @param companyId
   * @param companySpaceId
   * @param body
   */
  public updateCompanySpace (companyId: number, companySpaceId: number, body: {name: string}): Observable<CompanySpace> {
    return this.http
      .put(`/api/rms/companies/${companyId}/spaces/${companySpaceId}`, body).pipe(
      catchError(ex => this.exceptionsService.catchBadResponse(ex)));
  }

  /**
   * Creates a new company space
   * @param companyId
   * @param body
   */
  public createCompanySpace (companyId: number, body: {name: string}): Observable<CompanySpace> {
    return this.http
      .post(`/api/rms/companies/${companyId}/spaces`, body).pipe(
      catchError(ex => this.exceptionsService.catchBadResponse(ex)));
  }

  /**
   * Markes the selected space as default
   * @param {number} companyId
   * @param {number} companySpaceId
   * @returns {Observable<CompanySpace>}
   */
  public makeCompanySpaceAsDefault (companyId: number, companySpaceId: number): Observable<CompanySpace> {
    return this.http
      .post(`/api/rms/companies/${companyId}/spaces/${companySpaceId}/makedefault`, {}).pipe(
      catchError(ex => this.exceptionsService.catchBadResponse(ex)));
  }

  /**
   * Deactivates the company space
   * @param {number} companyId
   * @param {number} companySpaceId
   * @returns {Observable<CompanySpace>}
   */
  public deactivateCompanySpace (companyId: number, companySpaceId: number): Observable<CompanySpace> {
    return this.http
      .post(`/api/rms/companies/${companyId}/spaces/${companySpaceId}/deactivate`, {}).pipe(
      catchError(ex => this.exceptionsService.catchBadResponse(ex)));
  }

  /**
   * On drag and drop, updates the company space order
   * @param {number} companyId
   * @param body
   * @returns {Observable<CompanySpace>}
   */
  public sortCompanySpaces (companyId: number, body: Array<{id: number, order: number}>): Observable<CompanySpace> {
    return this.http
      .put(`/api/rms/companies/${companyId}/spaces/sort`, body).pipe(
      catchError(ex => this.exceptionsService.catchBadResponse(ex)));
  }

  /**
   * Migrate space between companies
   * @param {number} companyId: from source company
   * @param {number} sourceId: selected space to migrate
   * @param {number} targetCompanyId: companyId to migrate to
   * @param {boolean} keepSourceThesis: optional add `keepthesis` as query string, send it as true only in case both have thesis,
   *                  and the user prefers to keep the thesis for space is migrating from (source)
   * @returns {Observable<null>}: status code 204
   */
  public migrateCompanySpace (companyId: number, sourceId: number, targetCompanyId: number, keepSourceThesis: boolean): Observable<null> {
    return this.http
      .post(`${CONFIG.apiUrl}/rms/companies/${companyId}/spaces/${sourceId}/migrate/${targetCompanyId}?keepthesis=${keepSourceThesis}`, {}).pipe(
      catchError(ex => this.exceptionsService.catchBadResponse(ex)));
  }
}
